import { Controller } from '@hotwired/stimulus'
import { SubscriptionSubmitValidation } from './mixins/subscription_submit_validation'
import { SubscriptionRecipeCheckboxValidation } from './mixins/subscription_recipe_checkbox_validation'

class AdminSubscriptionUpdateValidationController extends Controller {
  static targets = [
    'submitButton',
    'statusField',
    'currentPeriodEndField',
    'invoiceAt',
    'reorderAt',
    'validationMessage',
  ]
  static values = {
    earliestDeliveryDate: String,
    latestDeliveryDate: String,
    currentStatus: String,
    subscriptionId: String,
  }

  connect() {
    this.boundRefreshDeliveryDateBounds =
      this.refreshDeliveryDateBounds.bind(this)
    document.addEventListener(
      'visibilitychange',
      this.boundRefreshDeliveryDateBounds
    )
    window.addEventListener('focus', this.boundRefreshDeliveryDateBounds)

    this.checkValidation()
    this.refreshDeliveryDateBounds()

    if (this.hasStatusFieldTarget) {
      this.statusFieldTarget.addEventListener('change', () =>
        this.checkValidation()
      )
    }

    if (this.hasCurrentPeriodEndFieldTarget) {
      this.currentPeriodEndFieldTarget.addEventListener('change', () => {
        this.checkValidation()
        this.calculateTimes()
      })
      this.currentPeriodEndFieldTarget.addEventListener('input', () => {
        this.checkValidation()
        this.debouncedCalculateTimes()
      })
    }
  }

  disconnect() {
    document.removeEventListener(
      'visibilitychange',
      this.boundRefreshDeliveryDateBounds
    )
    window.removeEventListener('focus', this.boundRefreshDeliveryDateBounds)
    clearTimeout(this.calculateTimesTimeout)
  }

  checkValidation() {
    if (!this.validateRecipeQuantities()) {
      this.disableSubmitForRecipes()
      return
    }

    const newStatus = this.hasStatusFieldTarget
      ? this.statusFieldTarget.value
      : this.currentStatusValue
    const newPeriodEnd = this.hasCurrentPeriodEndFieldTarget
      ? this.currentPeriodEndFieldTarget.value
      : null

    const isLive = newStatus === 'started' || newStatus === 'invoiced'
    const isUnpausing =
      this.currentStatusValue !== 'started' &&
      this.currentStatusValue !== 'invoiced' &&
      newStatus === 'started'

    if (!newPeriodEnd || (!isLive && !isUnpausing)) {
      this.enableSubmit()
      return
    }

    const earliestDate = this.parseLocalDate(this.earliestDeliveryDateValue)
    const latestDate = this.parseLocalDate(this.latestDeliveryDateValue)
    const newPeriodEndDate = this.parseLocalDate(newPeriodEnd)

    if (newPeriodEndDate < earliestDate) {
      this.disableSubmitForEarliestDate()
      return
    }

    if (newPeriodEndDate > latestDate) {
      this.disableSubmitForLatestDate()
      return
    }

    this.enableSubmit()
  }

  disableSubmitForEarliestDate() {
    const earliestDateStr = this.parseLocalDate(
      this.earliestDeliveryDateValue
    ).toLocaleDateString()
    this.setSubmitState(
      true,
      `To change this plan, the delivery date must be on or after ${earliestDateStr}`
    )
  }

  disableSubmitForLatestDate() {
    const latestDateStr = this.parseLocalDate(
      this.latestDeliveryDateValue
    ).toLocaleDateString()
    this.setSubmitState(
      true,
      `Delivery date cannot be later than ${latestDateStr}`
    )
  }

  calculateTimes() {
    this.fetchSchedule().then((data) => {
      if (!data || data.error) {
        this.hideTimes()
        return
      }
      this.applyDeliveryDateBounds(data)
      this.displayTimes(data.invoice_at_formatted, data.reorder_at_formatted)
      this.checkValidation()
    })
  }

  refreshDeliveryDateBounds() {
    if (document.hidden) return

    this.fetchSchedule().then((data) => {
      if (!data || data.error) return
      this.applyDeliveryDateBounds(data)
      this.checkValidation()
    })
  }

  // YYYY-MM-DD from Rails/iso8601 and from <input type="date"> must be local
  // calendar dates. `new Date("YYYY-MM-DD")` is UTC midnight, which is the
  // previous day in US timezones, so a date on the min or max of the allowed
  // window is compared as if it were one day outside it.
  parseLocalDate(value) {
    if (!value) return new Date(NaN)
    return new Date(`${value}T00:00:00`)
  }

  fetchSchedule() {
    if (!this.hasCurrentPeriodEndFieldTarget || !this.subscriptionIdValue) {
      return Promise.resolve(null)
    }

    const date = this.currentPeriodEndFieldTarget.value
    if (!date) {
      this.hideTimes()
      return Promise.resolve(null)
    }

    const url = `/admin/subscriptions/${this.subscriptionIdValue}/calculate_times?current_period_end=${encodeURIComponent(date)}`

    return fetch(url, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then((response) => response.json())
      .catch((error) => {
        console.error('Error calculating times:', error)
        return null
      })
  }

  applyDeliveryDateBounds(data) {
    if (data.earliest_delivery_date) {
      this.earliestDeliveryDateValue = data.earliest_delivery_date
      if (this.hasCurrentPeriodEndFieldTarget) {
        this.currentPeriodEndFieldTarget.min = data.earliest_delivery_date
      }
    }

    if (data.latest_delivery_date) {
      this.latestDeliveryDateValue = data.latest_delivery_date
      if (this.hasCurrentPeriodEndFieldTarget) {
        this.currentPeriodEndFieldTarget.max = data.latest_delivery_date
      }
    }
  }

  debouncedCalculateTimes() {
    clearTimeout(this.calculateTimesTimeout)
    this.calculateTimesTimeout = setTimeout(() => {
      this.calculateTimes()
    }, 300)
  }

  displayTimes(invoiceAt, reorderAt) {
    if (this.hasInvoiceAtTarget) {
      this.invoiceAtTarget.textContent = `Will invoice at: ${invoiceAt}`
      this.invoiceAtTarget.style.display = 'block'
    }
    if (this.hasReorderAtTarget) {
      this.reorderAtTarget.textContent = `Will reorder at: ${reorderAt}`
      this.reorderAtTarget.style.display = 'block'
    }
  }

  hideTimes() {
    if (this.hasInvoiceAtTarget) {
      this.invoiceAtTarget.style.display = 'none'
    }
    if (this.hasReorderAtTarget) {
      this.reorderAtTarget.style.display = 'none'
    }
  }
}

Object.assign(
  AdminSubscriptionUpdateValidationController.prototype,
  SubscriptionSubmitValidation,
  SubscriptionRecipeCheckboxValidation
)

export default AdminSubscriptionUpdateValidationController
